import { useCallback, useState } from "react";

import type { PropertySchemaType } from "~/common/types";
import type { JsonObject } from "~/common/types/graphql.scalars";
import type { PropertySchemaAssociation_CategoryFragment } from "~/features/admin/categories";
import {
  buildCustomColumn,
  isConnectedToMain as checkConnected,
  isEntityInstance,
  isRelationInstance,
  rebuildColumnWithUpdatedType,
  SheetPlanner,
} from "~/features/dataImportProjects/hooks";
import { PropertySchemasMapper } from "~/features/dataImportProjects/utils/propertySchemasMapper";

import { removeColumnsByCategoryLabel, updateCategoryLabelInSheets } from "./sheetOps";
import {
  type SchemaDesignerCategory,
  type SchemaDesignerCategoryCardinality,
  type SchemaDesignerCategoryInstance,
  type SheetColumn,
  type SheetState,
  schemaDesignerCategoryCardinalities,
} from "./types";

export function useDataSourceDesigner() {
  const [selectedCategories, setSelectedCategories] = useState<SchemaDesignerCategoryInstance[]>([]);
  const [sheets, setSheets] = useState<SheetState[]>([]);
  const [activeSheetName, setActiveSheet] = useState<string>("");
  const [showUnconnectedAlert, setShowUnconnectedAlert] = useState<boolean>(false);
  const [unconnectedCategories, setUnconnectedCategories] = useState<
    Array<{ id: string; name: string; i18nLabel?: JsonObject | null }>
  >([]);

  const addCategory = useCallback(
    (category: SchemaDesignerCategory) => {
      const existingCategoryCount = selectedCategories.filter((c) => c.category.id === category.id).length;
      const categoryIndex = existingCategoryCount + 1;
      const isFirstEntity = category.type === "entity" && selectedCategories.filter(isEntityInstance).length === 0;

      const instanceId = `${category.id}:${crypto.randomUUID()}`;
      const instanceLabel = `${category.name}${categoryIndex}`;

      let selectedCategory: SchemaDesignerCategoryInstance;

      if (category.type === "entity") {
        selectedCategory = {
          category,
          instanceId,
          instanceLabel,
          isMain: isFirstEntity,
        };
      } else {
        selectedCategory = {
          category,
          cardinality: schemaDesignerCategoryCardinalities.ONE_TO_ONE,
          instanceId,
          instanceLabel,
          isMain: false,
        };
      }

      // Auto-link matching categories for relations
      if (isRelationInstance(selectedCategory)) {
        const relationCategory = selectedCategory;
        const entityCategories = selectedCategories.filter(isEntityInstance);

        const sourceInstance = entityCategories.find(
          (c) => c.instanceId === relationCategory.sourceCategory?.instanceId,
        );
        const targetInstance = entityCategories.find(
          (c) => c.instanceId === relationCategory.targetCategory?.instanceId,
        );

        // Prefer source over target
        if (sourceInstance) {
          relationCategory.sourceCategory = sourceInstance;
        } else if (targetInstance) {
          relationCategory.targetCategory = targetInstance;
        }
      }

      setSelectedCategories((prev) => [...prev, selectedCategory]);
    },
    [selectedCategories],
  );

  const removeCategory = useCallback(
    (instanceId: string) => {
      const categoryToRemove = selectedCategories.find((c) => c.instanceId === instanceId);
      if (!categoryToRemove) {
        return;
      }
      setSelectedCategories((prev) => prev.filter((c) => c.instanceId !== instanceId));
      const labelToRemove = categoryToRemove.instanceLabel || categoryToRemove.category.name;
      setSheets((prev) => removeColumnsByCategoryLabel(prev, labelToRemove));
    },
    [selectedCategories],
  );

  const updateRelationSourceTarget = useCallback(
    (instanceId: string, sourceEntityId: string | null, targetEntityId: string | null) => {
      setSelectedCategories((prev) => {
        // Find source and target entity instances
        const sourceEntity = sourceEntityId
          ? prev.filter(isEntityInstance).find((c) => c.instanceId === sourceEntityId)
          : undefined;
        const targetEntity = targetEntityId
          ? prev.filter(isEntityInstance).find((c) => c.instanceId === targetEntityId)
          : undefined;

        return prev.map((cat) => {
          if (cat.instanceId !== instanceId || !isRelationInstance(cat)) {
            return cat;
          }

          return {
            ...cat,
            sourceCategory: sourceEntity ?? undefined,
            targetCategory: targetEntity ?? undefined,
          };
        });
      });
    },
    [],
  );

  const updateRelationCardinality = useCallback(
    (instanceId: string, cardinality: SchemaDesignerCategoryCardinality) => {
      setSelectedCategories((prev) =>
        prev.map((cat) => (cat.instanceId === instanceId && isRelationInstance(cat) ? { ...cat, cardinality } : cat)),
      );
    },
    [],
  );

  const setMainCategory = useCallback((instanceId: string) => {
    setSelectedCategories((prev) =>
      prev.map((cat) => ({ ...cat, isMain: isEntityInstance(cat) && cat.instanceId === instanceId })),
    );
  }, []);

  const updateCategoryName = useCallback(
    (instanceId: string, newLabel: string) => {
      const categoryToUpdate = selectedCategories.find((c) => c.instanceId === instanceId);
      if (!categoryToUpdate) return;
      const oldLabel = categoryToUpdate.instanceLabel || categoryToUpdate.category.name;
      setSelectedCategories((prev) =>
        prev.map((cat) => (cat.instanceId === instanceId ? { ...cat, instanceLabel: newLabel } : cat)),
      );
      setSheets((prev) => updateCategoryLabelInSheets(prev, oldLabel, newLabel));
    },
    [selectedCategories],
  );

  const updateColumnLabel = useCallback((columnId: string, newLabel: string) => {
    setSheets((prev) =>
      prev.map((sheet) => {
        const hasColumn = sheet.columns.some((c) => c.columnId === columnId);
        if (!hasColumn) {
          return sheet;
        }

        // Check if the new name already exists in this sheet (excluding the current column)
        const isDuplicate = sheet.columns.some((c) => c.columnId !== columnId && c.columnLabel === newLabel);
        if (isDuplicate) {
          throw new Error(`Column name "${newLabel}" already exists in this sheet`);
        }

        return {
          ...sheet,
          columns: sheet.columns.map((c) => (c.columnId === columnId ? { ...c, columnLabel: newLabel } : c)),
        };
      }),
    );
  }, []);

  const updateColumnDescription = useCallback((columnId: string, newDescription: string) => {
    setSheets((prev) =>
      prev.map((sheet) => ({
        ...sheet,
        columns: sheet.columns.map((c) =>
          c.columnId === columnId ? { ...c, property: { ...c.property, description: newDescription } } : c,
        ),
      })),
    );
  }, []);

  const updateColumnSchemaType = useCallback(
    (columnId: string, newType: PropertySchemaType, selectOptions?: Array<{ label: string; value: string }>) => {
      setSheets((prev) =>
        prev.map((sheet) => ({
          ...sheet,
          columns: sheet.columns.map((c) =>
            c.columnId === columnId ? rebuildColumnWithUpdatedType(c, newType, selectOptions) : c,
          ),
        })),
      );
    },
    [],
  );

  const toggleColumnRequired = useCallback((columnId: string) => {
    setSheets((prev) =>
      prev.map((sheet) => ({
        ...sheet,
        columns: sheet.columns.map((c) =>
          c.columnId === columnId ? { ...c, property: { ...c.property, required: !c.property.required } } : c,
        ),
      })),
    );
  }, []);

  const moveColumn = useCallback((columnId: string, direction: "up" | "down") => {
    setSheets((prev) =>
      prev.map((sheet) => {
        const allColumns = [...sheet.columns];

        // Find the column to move in current visible order
        const idx = allColumns.findIndex((c) => c.columnId === columnId);
        if (idx === -1) {
          return sheet;
        }

        // Check bounds
        if ((direction === "up" && idx === 0) || (direction === "down" && idx === allColumns.length - 1)) {
          return sheet;
        }

        // Calculate new position
        const newIdx = direction === "up" ? idx - 1 : idx + 1;

        // Reorder the array
        const reorderedColumns = [...allColumns];
        const [moved] = reorderedColumns.splice(idx, 1);
        reorderedColumns.splice(newIdx, 0, moved);

        // Update order values to reflect new visual order
        const updatedColumns = reorderedColumns.map((c, i) => ({ ...c, order: i }));

        return { ...sheet, columns: updatedColumns };
      }),
    );
  }, []);

  const removeColumn = useCallback((columnId: string) => {
    setSheets((prev) =>
      prev.map((sheet) => ({
        ...sheet,
        columns: sheet.columns.filter((c) => c.columnId !== columnId),
      })),
    );
  }, []);

  const duplicateColumn = useCallback((columnId: string) => {
    setSheets((prev) =>
      prev.map((sheet) => {
        const columnIndex = sheet.columns.findIndex((c) => c.columnId === columnId);
        if (columnIndex === -1) return sheet;

        const original = sheet.columns[columnIndex];
        if (original.columnKind === "foreignKey") return sheet;

        const baseName = original.columnLabel;
        let suffix = 2;
        while (sheet.columns.some((c) => c.columnLabel === `${baseName}_${suffix}`)) {
          suffix++;
        }
        const newLabel = `${baseName}_${suffix}`;

        const newColumn: SheetColumn = {
          ...original,
          columnId: crypto.randomUUID(),
          columnLabel: newLabel,
          columnPath: `${original.sheetName}!${newLabel}`,
          order: original.order + 1,
        };

        const newColumns = [...sheet.columns];
        newColumns.splice(columnIndex + 1, 0, newColumn);
        return {
          ...sheet,
          columns: newColumns.map((c, i) => ({ ...c, order: i })),
        };
      }),
    );
  }, []);

  const updateColumnProperty = useCallback(
    (columnId: string, association: PropertySchemaAssociation_CategoryFragment | null) => {
      setSheets((prev) =>
        prev.map((sheet) => ({
          ...sheet,
          columns: sheet.columns.map((c) => {
            if (c.columnId !== columnId) return c;
            if (association === null) {
              // Clear mapping: keep categoryId, clear property name
              return {
                ...c,
                property: {
                  ...c.property,
                  name: "",
                },
              };
            }
            // Change mapping: replace property with new association's form data
            const formData = PropertySchemasMapper.propertySchemaAssociation.toFormData(association);
            return { ...c, property: formData };
          }),
        })),
      );
    },
    [],
  );

  const addCustomColumn = useCallback(() => {
    setSheets((prev) => {
      if (!activeSheetName) {
        return prev;
      }
      return prev.map((sheet) => {
        if (sheet.name !== activeSheetName) {
          return sheet;
        }
        const nextOrder = sheet.columns.length;
        const newCol = buildCustomColumn(sheet.name, nextOrder);
        return { ...sheet, columns: [...sheet.columns, newCol] };
      });
    });
  }, [activeSheetName]);

  const handleUpdatePlan = useCallback(async () => {
    if (selectedCategories.length === 0) {
      return;
    }
    const hasMainCategory = selectedCategories.some((c) => c.isMain);
    if (hasMainCategory) {
      const unconnected = selectedCategories.filter(
        (category) => !category.isMain && !checkConnected(selectedCategories, category.instanceId),
      );
      if (unconnected.length > 0) {
        setUnconnectedCategories(
          unconnected.map((c) => ({
            id: c.instanceId,
            name: c.instanceLabel || c.category.name,
            i18nLabel: c.category.i18nLabel,
          })),
        );
        setShowUnconnectedAlert(true);
        return;
      }
    }
    const sheetPlans = SheetPlanner.planSheets(selectedCategories);
    setSheets(sheetPlans);
  }, [selectedCategories]);

  const closeUnconnectedAlert = useCallback(() => setShowUnconnectedAlert(false), []);

  return {
    // state
    selectedCategories,
    sheets,
    activeSheetName,
    showUnconnectedAlert,
    unconnectedCategories,
    // setters
    setActiveSheet,
    // category ops
    addCategory,
    removeCategory,
    updateRelationSourceTarget,
    updateRelationCardinality,
    setMainCategory,
    updateCategoryName,
    // column ops
    updateColumnLabel,
    updateColumnDescription,
    updateColumnSchemaType,
    toggleColumnRequired,
    moveColumn,
    removeColumn,
    duplicateColumn,
    updateColumnProperty,
    addCustomColumn,
    // plan ops
    handleUpdatePlan,
    closeUnconnectedAlert,
  } as const;
}
